import React from 'react';
import _ from 'underscore';

import {SequenceSchemaType} from 'constants/RegistryConstants';
import StaticSchemaInterfaceFieldMapping, {
  StaticInterfaceField,
  StaticSchemaInterfaceFieldDefinition,
} from 'schemas/static-schema-interface/StaticSchemaInterfaceFieldMapping';
import {SettingsLabel} from 'ui-library/SettingsField';

import {TagSchemaSummaryGraphQL} from '../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {EXTENDED_CONJUGATE_FOLDER_ITEM_TYPES} from '../ComplexPolymerSchemaConfiguration';
import {DraftImplementedOligoDuplexInterface} from '../ComplexPolymerSchemaInterfaceConstants';

export const FORWARD_STRAND_FIELD_DEFINITION: StaticSchemaInterfaceFieldDefinition = {
  key: 'forwardStrandSchemaFieldSpec',
  displayName: 'Forward strand',
  placeholder: 'Select oligo schema',
  canMapToMultipleValues: false,
  isRequired: true,
  schemaCriteria: {
    allowedFolderItemTypes: [SequenceSchemaType.OLIGO, SequenceSchemaType.RNA_OLIGO],
  },
};

export const REVERSE_STRAND_FIELD_DEFINITION: StaticSchemaInterfaceFieldDefinition = {
  key: 'reverseStrandSchemaFieldSpec',
  displayName: 'Reverse strand',
  placeholder: 'Select oligo schema',
  canMapToMultipleValues: false,
  isRequired: true,
  schemaCriteria: {
    allowedFolderItemTypes: [SequenceSchemaType.OLIGO, SequenceSchemaType.RNA_OLIGO],
  },
};

export function getConjugatesFieldDefinition(): StaticSchemaInterfaceFieldDefinition {
  return {
    key: 'conjugatesSchemaFieldSpecs',
    displayName: 'Components',
    placeholder: 'Select component schemas',
    canMapToMultipleValues: true,
    schemaCriteria: {
      allowedFolderItemTypes: EXTENDED_CONJUGATE_FOLDER_ITEM_TYPES,
    },
  };
}

export interface OligoDuplexConfigurationTableProps {
  oligoDuplexImplementedInterface: DraftImplementedOligoDuplexInterface;
  disabled: boolean;
  onChange: (config: DraftImplementedOligoDuplexInterface) => void;
  /* All tag schemas that currently exist on the registry */
  schemas: Array<TagSchemaSummaryGraphQL>;
}

function resolveForwardAndReverseStrandSpecConflicts(
  implementedInterface: DraftImplementedOligoDuplexInterface
): DraftImplementedOligoDuplexInterface {
  const forwardSpec = implementedInterface?.forwardStrandSchemaFieldSpec;
  const reverseSpec = implementedInterface?.reverseStrandSchemaFieldSpec;

  if (forwardSpec?.schemaFieldName != null && forwardSpec.schemaFieldName === reverseSpec?.schemaFieldName) {
    return {
      ...implementedInterface,
      forwardStrandSchemaFieldSpec: {
        ...forwardSpec,
        schemaFieldName: `${forwardSpec.schemaFieldName} (Forward)`,
      },
      reverseStrandSchemaFieldSpec: {
        ...reverseSpec,
        schemaFieldName: `${reverseSpec.schemaFieldName} (Reverse)`,
      },
    };
  }
  return implementedInterface;
}

function resolveConjugatesSpecConflicts(
  implementedInterface: DraftImplementedOligoDuplexInterface
): DraftImplementedOligoDuplexInterface {
  const strandSchemaFieldNames = [
    implementedInterface?.forwardStrandSchemaFieldSpec?.schemaFieldName,
    implementedInterface?.reverseStrandSchemaFieldSpec?.schemaFieldName,
  ];
  const conjugateSpecs = implementedInterface?.conjugatesSchemaFieldSpecs;
  const updatedConjugateSpecs = [];

  for (const conjugateSpec of conjugateSpecs ?? []) {
    if (
      conjugateSpec.schemaFieldName != null &&
      strandSchemaFieldNames.includes(conjugateSpec.schemaFieldName)
    ) {
      updatedConjugateSpecs.push({
        ...conjugateSpec,
        schemaFieldName: `${conjugateSpec.schemaFieldName} (Conjugate)`,
      });
    } else {
      updatedConjugateSpecs.push(conjugateSpec);
    }
  }

  return {
    ...implementedInterface,
    conjugatesSchemaFieldSpecs: updatedConjugateSpecs,
  };
}

export default function OligoDuplexConfigurationTable(
  props: OligoDuplexConfigurationTableProps
): JSX.Element {
  const schemasById = _.indexBy(props.schemas, 'id');
  const onChange = ({fieldDefinition, values}: StaticInterfaceField): void => {
    if (values == null) {
      props.onChange({
        ...props.oligoDuplexImplementedInterface,
        [fieldDefinition.key]: null,
      });
      return;
    }

    if (
      fieldDefinition.key === FORWARD_STRAND_FIELD_DEFINITION.key ||
      fieldDefinition.key === REVERSE_STRAND_FIELD_DEFINITION.key
    ) {
      const spec =
        values.length === 0
          ? null
          : {
              schemaFieldName: schemasById[values[0]].name,
              schemaOrInterfaceId: values[0],
            };
      const newImplementedInterface = {
        ...props.oligoDuplexImplementedInterface,
        [fieldDefinition.key]: spec,
      };
      props.onChange(resolveForwardAndReverseStrandSpecConflicts(newImplementedInterface));
    } else {
      const newImplementedInterface = {
        ...props.oligoDuplexImplementedInterface,
        [fieldDefinition.key]: values.map((value) => ({
          schemaFieldName: schemasById[value].name,
          schemaOrInterfaceId: value,
        })),
      };
      props.onChange(resolveConjugatesSpecConflicts(newImplementedInterface));
    }
  };
  const {forwardStrandSchemaFieldSpec, reverseStrandSchemaFieldSpec, conjugatesSchemaFieldSpecs} =
    props.oligoDuplexImplementedInterface;
  const staticFields = [
    {
      fieldDefinition: FORWARD_STRAND_FIELD_DEFINITION,
      values:
        forwardStrandSchemaFieldSpec?.schemaOrInterfaceId != null
          ? [forwardStrandSchemaFieldSpec.schemaOrInterfaceId]
          : null,
    },
    {
      fieldDefinition: REVERSE_STRAND_FIELD_DEFINITION,
      values:
        reverseStrandSchemaFieldSpec?.schemaOrInterfaceId != null
          ? [reverseStrandSchemaFieldSpec.schemaOrInterfaceId]
          : null,
    },
    {
      fieldDefinition: getConjugatesFieldDefinition(),
      values: conjugatesSchemaFieldSpecs?.map((spec) => spec.schemaOrInterfaceId) ?? null,
    },
  ];
  return (
    <StaticSchemaInterfaceFieldMapping
      disabled={props.disabled}
      onChange={onChange}
      schemas={props.schemas}
      staticFields={staticFields}
      implementedStaticSchemaInterface={props.oligoDuplexImplementedInterface}
      subtitle={
        <p>
          Please select the allowed schemas for the <b>forward strand</b>, the <b>reverse strand</b>, and any
          attached <b>components</b>.
        </p>
      }
      title={<SettingsLabel label="Structural configuration" />}
    />
  );
}
